import React, { useMemo, useCallback, useState, useRef, useEffect } from 'react'
import { hierarchy } from 'd3-hierarchy'
import { scaleSqrt } from 'd3-scale'
import styled from '@emotion/styled'
import { css } from '@emotion/core'
import Color from 'color'
import { pickBy } from 'lodash'
import { useHistory, useLocation } from 'react-router-dom'

// theme
import theme from 'common/styles/theme'

// components
import BreadCrumb from 'components/BreadCrumb'
import TopBar from 'components/TopBar'
import Graph from 'components/Graph'
import GraphSettingsBar, { GraphCommand, GraphCameraReset } from 'components/GraphSettingsBar'
import Panel from 'components/Panel'

// data
import lmTree from 'data/output/lm-tree.json'
import { dictFromTree } from 'data/scripts/tree/main'
import concepts from 'data/output/concept-nodes.json'
import allEdges from 'data/output/concept-deduped-links.json'
import lmToConceptDict from 'data/output/lm-to-concept-dict.json'
import conceptSourceToLinkIndex from 'data/output/concept-source-to-links-index.json'
import conceptTargetToLinkIndex from 'data/output/concept-target-to-links-index.json'
import idToNature from 'data/output/id-to-nature.json'

import defaultPositions from 'data/snippet/default-position.json'

// hooks
import { useCurrentNode, nodeIdToIndex, useCurrentNodeIsRoot } from 'flow/queryparams'
import { useSelection, useSearch, useHighlightedLegendItem, useHighlightedOnGraphId } from 'flow/interactions/accessors'
import { useConceptColorsById } from 'flow/settings/color'
import { useShowRelation } from 'flow/settings/relations'
import useFilteredRelations from 'flow/graph/useFilteredRelations'
import useSelectedNodesIds, { useCurrentIsConcept } from 'flow/graph/useSelectedNodes'
import { useMedia } from 'hooks/custom/useMedia'

// utils
import arrayToDic from 'common/utils/array-to-dic'
import getNature from 'common/utils/get-nature'

// Learning Map tree with ancestors and children
const tree = hierarchy(lmTree)
const lmDict = dictFromTree(tree)

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`

const ChartsContainer = styled.div`
  display: flex;
  flex: 1;  
  align-items: stretch;
  overflow: hidden; /* for the right column to handle overflow-y: auto */
`
const GraphContainer = styled.div(({ theme }) => css`
  position: relative;
  width: 100%;
  height: 100%;
  background: ${theme.purple};
`)

const Main = () => {
  const nodeId = useCurrentNode()
  const currentNode = useMemo(() => {
    return nodeId ? lmDict[nodeId] : tree
  }, [nodeId])

  return (
    <Container>
      <TopBar />
      <BreadCrumb currentNode={currentNode} />
      <ChartsContainer>
        <GraphProxy currentNode={nodeId} />
        <Panel currentNode={currentNode} />
      </ChartsContainer>
    </Container>
  )
}

const basicFA2params = {
  edgeWeightInfluence: 0.9,
  barnesHutTheta: 2,
  gravity: 0.7,
  scalingRatio: 2
}
const smallGraphFA2params = {
  edgeWeightInfluence: 0.1,
  barnesHutTheta: 2,
  gravity: 0.5,
  scalingRatio: 2
}

const sizeScale = scaleSqrt()
  .domain([0, 100])
  .range([0.025, 1])

const nodesLengthWidthoutCompetencies = concepts
  .filter((d) => d.type !== 'competency')
  .length

const edgesLengthWidthoutCompetencies = allEdges
  .filter((d) => d.type !== 'competency')
  .length

const edgeIdToIndex = allEdges.reduce((p, c, i) => {
  p[c.id] = i
  return p
}, {})

const natureToZoom = {
  goal: 0.5,
  grade: 1.5,
  subject: 2,
  unit: 3,
  chapter: 5,
  topic: 5.5,
  concept: 7
}

const primaryOntologyLabelColor = Color(theme.blueDeep).alpha(theme.labelOpacity).toString()
const secondaryOntologyLabelColor = Color(theme.blueDeep).alpha(theme.labelOpacity).toString()
const thirdOntologyLabelColor = Color(theme.blueDeep).alpha(theme.labelOpacity).toString()
const conceptLabelColor = Color(theme.blueUltraDark).alpha(theme.labelOpacity).toString()

const ontologyLabelsZoom = {
  knowledge_god: [null, null],
  discipline: [0.5, 10],
  field: [2, 10],
  cluster: [4, 10]
}
const ontologyLabelsParams = {
  discipline: {
    size: 11,
    uppercase: true,
    bold: true,
    color: primaryOntologyLabelColor
  },
  field: {
    size: 10,
    uppercase: true,
    bold: false,
    color: secondaryOntologyLabelColor
  },
  cluster: {
    size: 9,
    uppercase: false,
    bold: false,
    color: thirdOntologyLabelColor
  }
}

const states = {
  node: {
    hightlighted: {
      opacity: 1.0
    },
    hidden: {
      opacity: 0.2
    }
  }
}

const edgesOpacity = {
  10: 0.3,
  500: 0.25,
  5000: 0.15,
  50000: 0.1
}

const nodeShapes = concepts.reduce((p, c) => {
  p[c.id] = c.type === 'competency' ? 'triangle' : 'hex'
  return p
}, {})

const isConceptOrCompetency = (id) => ['concept', 'competency'].includes(getNature(id))

function GraphProxy ({ currentNode }) {
  const currentNodeIsRoot = useCurrentNodeIsRoot()
  const currentNodeIsConcept = useCurrentIsConcept()
  const graphContainerRef = useRef()
  const history = useHistory()
  const { search } = useLocation()
  const [defaultCameraPos, setDefaultCameraPos] = useState({
    x: 0, y: 0
  })

  useEffect(() => {
    if (currentNodeIsRoot) {
      setDefaultCameraPos({ x: 0, y: 0 })
    }
  }, [currentNodeIsRoot])

  const defaultCameraZoom = useMemo(() => {
    return {
      k: natureToZoom[idToNature[currentNode]]
    }
  }, [currentNode])

  const [rendering, setRendering] = useState(true)
  const [highlightedGraphId, setHighlightedGraphId] = useHighlightedOnGraphId()
  const [selection, setSelection] = useSelection()
  const [searchedId] = useSearch()

  const [highlightedLegendItem] = useHighlightedLegendItem()

  const {
    nodeDegrees,
    ontologyEdgeIds,
    competencyEdgeIds,
    filteredLinksWithUserSettings,
    filteredLinksWithUserSettingsValues,
    count
  } = useFilteredRelations()

  const [showRelations] = useShowRelation()
  const nodeColors = useConceptColorsById()
  const selectedNodes = useSelectedNodesIds()

  // First we need to apply some rules to the current selection
  const primaryItem = useMemo(() => {
    if (highlightedGraphId) {
      return {
        id: highlightedGraphId,
        isConceptOrCompetency: true
      }
    }
    if (searchedId) {
      return {
        id: searchedId,
        isConceptOrCompetency: isConceptOrCompetency(searchedId)
      }
    }
    if (!selection.length) return null
    return {
      id: selection[0].id,
      isConceptOrCompetency: isConceptOrCompetency(selection[0].id)
    }
  }, [selection, highlightedGraphId, searchedId])

  const secondaryItem = useMemo(() => {
    // Ignore all other interactions if
    // there is an interaction on the graph
    if (highlightedGraphId) return null
    // if a search is currently happening
    if (searchedId) return null
    // if there is not second element in selection
    if (selection.length < 2) return null
    return {
      id: selection[1].id,
      isConceptOrCompetency: isConceptOrCompetency(selection[1].id)
    }
  }, [selection, highlightedGraphId, searchedId])

  const [completeHightlightedNodesDict, connectedEdges] = useMemo(() => {
    // Part 1:
    // ********
    //
    // Case 1: We want to show a node and its connected nodes
    // ------------------------------------------------------
    // This case happens when the user is hovering the graph (we then ignore other interactions)
    // OR 1 and only 1 concept is hovered/selected in the column or search bar is a concept
    //
    // Case 2: We want to show a subset of the nodes
    // ------------------------------------------------------
    // This case happens when the user has 1 and only 1 interaction and that interaction is NOT a concept/competency
    // OR search bar is NOT a concept
    //
    // Case 3: We want to show the relation bewteen two subsets
    // ------------------------------------------------------
    // This case happens when the user has 2 interactions.
    // These interactions could be on an element of the LM OR a concept.

    // Case 4: We want to display everything
    // ------------------------------------------------------
    // We don't have any interaction

    let nodes = {}
    let connectedEdges = {}
    // case 1
    if (primaryItem?.isConceptOrCompetency && !secondaryItem) {
      const nodesArray = []
      nodesArray.push(primaryItem.id)
      const sources = conceptTargetToLinkIndex[primaryItem.id] || []
      const targets = conceptSourceToLinkIndex[primaryItem.id] || []
      targets.forEach((d) => {
        if (allEdges[d]) {
          nodesArray.push(allEdges[d].target)
          connectedEdges[allEdges[d].id] = 1
        }
      })
      sources.forEach((d) => {
        if (allEdges[d]) {
          nodesArray.push(allEdges[d].source)
          connectedEdges[allEdges[d].id] = 1
        }
      })
      nodes = arrayToDic(nodesArray)
    // case 2
    } else if (primaryItem && !secondaryItem) {
      nodes = arrayToDic(lmToConceptDict[primaryItem.id] || primaryItem.id)
      allEdges.forEach((link) => {
        if (nodes[link.source] && nodes[link.target]) {
          connectedEdges[link.id] = 1
        }
      })
    // case 3
    } else if (primaryItem && secondaryItem) {
      const highlightedPrimaryConcepts = lmToConceptDict[primaryItem.id]
      const highlightedSecondaryConcepts = lmToConceptDict[secondaryItem.id]

      // Nodes
      const nodesA = arrayToDic(highlightedPrimaryConcepts || [primaryItem.id])
      const nodesB = arrayToDic(highlightedSecondaryConcepts || [secondaryItem.id])
      nodes = { ...nodesA, ...nodesB }

      // Links
      allEdges.forEach((link) => {
        if ((nodesA[link.source] && nodesB[link.target]) || (nodesB[link.source] && nodesA[link.target])) {
          connectedEdges[link.id] = 1
        }
      })
    }

    // Part 2:
    // ********
    // We now have a list of nodes and links to display.
    // We then check if the use is currenlty hovering the legend
    // If it's true, we remove the concepts that do not match
    // the hovered legend
    if (highlightedLegendItem.conceptsIds) {
      const dict = arrayToDic(highlightedLegendItem.conceptsIds)
      nodes = primaryItem ? pickBy(nodes, (v, id) => dict[id]) : dict
      connectedEdges = {}
      allEdges.forEach((link) => {
        if (nodes[link.source] && nodes[link.target]) {
          connectedEdges[link.id] = 1
        }
      })
    }

    return [
      nodes,
      connectedEdges
    ]
  }, [primaryItem, secondaryItem, highlightedLegendItem])

  const hasInteraction = useMemo(() => {
    return highlightedGraphId || selection.length
  }, [highlightedGraphId, selection.length])

  const hasFiltering = useMemo(() => {
    return highlightedGraphId || selection.length || highlightedLegendItem.conceptsIds || searchedId
  }, [highlightedGraphId, highlightedLegendItem.conceptsIds, selection.length, searchedId])

  const [nodeParams, labels, conceptsCount] = useMemo(() => {
    let conceptsCount = 0
    const labels = []

    const params = []

    selectedNodes.forEach((id) => {
      const node = concepts[nodeIdToIndex[id]]
      const degree = nodeDegrees[id] || 0
      const highlighted = !hasFiltering || completeHightlightedNodesDict[id]
      const isOntology = node?.type === 'ontology'
      const isCompetency = node?.type === 'competency'
      const scale = isOntology
        ? 0.001
        : isCompetency
          ? 0.3
          : 0.3 + sizeScale(degree)

      if (isOntology && !isCompetency && node.ontology_level !== 'knowledge_god') {
        labels.push({
          id: id,
          value: ontologyLabelsParams[node.ontology_level].uppercase
            ? node.label.toUpperCase()
            : node.label,
          zoom: currentNodeIsRoot
            ? ontologyLabelsZoom[node.ontology_level]
            : [0, 10],
          color: ontologyLabelsParams[node.ontology_level].color,
          fontSize: ontologyLabelsParams[node.ontology_level].size,
          bold: ontologyLabelsParams[node.ontology_level].bold,
          strokeWidth: 1
        })
      }

      if (!defaultPositions[id]) {
        defaultPositions[id] = [
          (Math.random() - 0.5) * 1000,
          (Math.random() - 0.5) * 1000,
          0
        ]
      }

      params.push({
        id,
        shape: nodeShapes[id],
        defaultPosition: defaultPositions[id],
        style: {
          opacity: highlighted ? states.node.hightlighted.opacity : states.node.hidden.opacity,
          scale: scale,
          color: isCompetency ? [1, 1, 1] : nodeColors[id],
          type: 'node'
        },
        fa2: {
          mass: 1 + degree,
          size: 1 + degree
        }
      })

      if (!isCompetency && !isOntology) conceptsCount += 1
    })

    if (primaryItem?.isConceptOrCompetency) {
      labels.push({
        id: primaryItem.id,
        value: concepts[nodeIdToIndex[primaryItem.id]]?.label || "" ,
        zoom: [0, 10],
        color: conceptLabelColor,
        strokeWidth: 0
      })
    } else if (currentNodeIsConcept) {
      labels.push({
        id: currentNode,
        value: concepts[nodeIdToIndex[currentNode]]?.label || "",
        zoom: [0, 10],
        color: conceptLabelColor,
        strokeWidth: 0
      })
    }

    return [
      params,
      labels,
      conceptsCount
    ]
  }, [hasFiltering, currentNode, currentNodeIsConcept, completeHightlightedNodesDict, primaryItem, selectedNodes, nodeColors, nodeDegrees, currentNodeIsRoot])

  const edgesParams = useMemo(() => {
    const relativeOpacity = Object.keys(edgesOpacity).reduce((p, c) => {
      if (parseFloat(c) < filteredLinksWithUserSettings.length) return edgesOpacity[c]
      return p
    }, 1)

    const params = []

    filteredLinksWithUserSettings.forEach((id) => {
      const isCompetency = competencyEdgeIds[id]
      const isOntology = ontologyEdgeIds[id]
      const { source, target } = allEdges[edgeIdToIndex[id]]
      let opacity = null

      if (isOntology) {
        opacity = 0
      } else {
        if (hasFiltering) {
          opacity = connectedEdges[id] ? relativeOpacity * 1.5 : 0.04
        } else {
          opacity = relativeOpacity
        }
      }

      params.push({
        id,
        source,
        target,
        style: {
          opacity: isCompetency ? opacity * 0.5 : opacity,
          width: isCompetency ? 0.18 : 0.35
        },
        fa2: {
          value: isOntology
            ? 20
            : filteredLinksWithUserSettingsValues[id] * 10
        }
      })
    })

    return params
  }, [hasFiltering, connectedEdges, ontologyEdgeIds, competencyEdgeIds, filteredLinksWithUserSettings, filteredLinksWithUserSettingsValues])

  const resetZoom = useCallback(
    () => {
      setDefaultCameraPos({ x: 0, y: 0 })
    }, [])

  const onNodeOver = useCallback(
    (id) => {
      if (id && concepts[nodeIdToIndex[id]]?.type !== 'ontology') {
        if (id && concepts[nodeIdToIndex[id]]?.type !== 'competency') {
          graphContainerRef.current.style.cursor = 'pointer'
        } else {
          graphContainerRef.current.style.cursor = 'auto'
        }

        if (id !== highlightedGraphId) {
          setHighlightedGraphId(id)
        }
      } else {
        graphContainerRef.current.style.cursor = 'auto'
        setHighlightedGraphId(null)
      }
    },
    [graphContainerRef, highlightedGraphId, setHighlightedGraphId]
  )

  const onNodeClick = useCallback(
    (id) => {
      return
      if (id && concepts[nodeIdToIndex[id]].type !== 'competency' && concepts[nodeIdToIndex[id]].type !== 'ontology') {
        history.push(`/${id}${search}`)
        resetZoom()
      }
      if (!id) setSelection([])
    },
    [search, history, resetZoom, setSelection]
  )

  const FA2params = useMemo(() => {
    if (selectedNodes.length > 1000) {
      return basicFA2params
    } else {
      return smallGraphFA2params
    }
  }, [selectedNodes])

  const isMobile = useMedia("(max-width: 768px)")

  return (
    <GraphContainer ref={graphContainerRef}>
      <Graph
        FA2params={FA2params}
        rendering={rendering}
        runLerp={!hasInteraction}

        defaultCameraPosition={defaultCameraPos}
        defaultCameraZoom={defaultCameraZoom}

        hideEdges={!showRelations && !hasInteraction}

        maxNodes={nodesLengthWidthoutCompetencies}
        nodes={nodeParams}
        onNodeOver={onNodeOver}
        onNodeClick={onNodeClick}

        maxEdges={edgesLengthWidthoutCompetencies}
        edges={edgesParams}

        labels={labels}
      />

      {!isMobile && <GraphSettingsBar
        count={count}
        conceptsCount={conceptsCount}
        relationsCount={count.categories.attributional + count.categories.peer}
      >
        <GraphCameraReset
          onClick={resetZoom}
        />
        <GraphCommand
          rendering={rendering}
          onToggleRendering={setRendering}
        />
      </GraphSettingsBar>}
    </GraphContainer>
  )
}

export default Main
